import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { faCheck, faClose, faTicket, faXmark } from "@fortawesome/free-solid-svg-icons"
import { ButtonIcon } from "../../components/Button/Button"
import List from "../../components/List/List"
import { AdminListCard, AdminListMetricItem } from "./components/AdminListCard"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { convertToLocalMoney, formatDateToString } from "../../Objects/Generics/Helps"
import { EPlanCouponDiscountType } from "../../Enum/EPlanCouponDiscountType"
import { EPlanCouponApprovalStatus } from "../../Enum/EPlanCouponApprovalStatus"
import { AdminPlanCoupon } from "../../Objects/AdminPlan/AdminPlanCoupon"
import { useAdminPlan } from "../../hooks/AdminDashboard/useAdminPlan"

interface ResellerCouponsPopupProps {
  resellerId: number
  onClose: () => void
}

const formatDiscount = (coupon: AdminPlanCoupon) =>
  coupon.discountType === EPlanCouponDiscountType.Percentage
    ? `${coupon.discountValue}%`
    : convertToLocalMoney(coupon.discountValue)

export const ResellerCouponsPopup: React.FC<ResellerCouponsPopupProps> = ({ resellerId, onClose }) => {
  const { t } = useTranslation(["admin", "frontend"])

  const { handleGetAllCoupons, handleApproveCoupon, handleRejectCoupon } = useAdminPlan()

  const [coupons, setCoupons] = useState<AdminPlanCoupon[]>([])

  const refresh = () =>
    handleGetAllCoupons((all) => setCoupons(all.filter((e) => e.requestedByResellerEntityId === resellerId)))

  useEffect(() => {
    refresh()
  }, [resellerId])

  const statusLabel = (status: EPlanCouponApprovalStatus) => {
    if (status === EPlanCouponApprovalStatus.Approved)
      return t("frontend:admin_dashboard.plans_and_coupons.coupon_filter_active")
    if (status === EPlanCouponApprovalStatus.Pending)
      return t("frontend:admin_dashboard.plans_and_coupons.coupon_filter_pending")

    return t("frontend:admin_dashboard.plans_and_coupons.coupon_filter_rejected")
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={`${defaultStyle.alert} max-h-[85vh] overflow-y-auto`}>
            <TitlePageResponsiveIconSmall
              title={t("reseller_coupons_title")}
              icon={faTicket}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.resellerCouponsPopup.button.onClose"
                />
              }
            />

            <div className="mt-2 p-2">
              <List
                showFilterStatus={false}
                messageCount={t("reseller_coupons_count")}
                messageEmpty={t("reseller_coupons_empty")}
                nameFindSearch={t("reseller_coupons_search")}
                handleOnClick={() => {}}
                data={coupons.map((item, index) => {
                  const isPending = item.approvalStatus === EPlanCouponApprovalStatus.Pending

                  return {
                    id: item.id,
                    index,
                    title: item.code,
                    linkImage: "",
                    dataFilter: [item.code],
                    children: (
                      <AdminListCard
                        title={item.code}
                        subtitle={formatDiscount(item)}
                        badge={statusLabel(item.approvalStatus)}
                        badgeClass={
                          item.approvalStatus === EPlanCouponApprovalStatus.Approved
                            ? "bg-emerald-100 text-emerald-700"
                            : isPending
                              ? "bg-amber-100 text-amber-700"
                              : "bg-red-100 text-red-700"
                        }
                        cardClass="rounded-xl border-2 border-slate-200 bg-slate-50 p-3"
                      >
                        <div className="gap-2">
                          <AdminListMetricItem
                            label={t("frontend:admin_dashboard.plans_and_coupons.coupon_discount")}
                            value={formatDiscount(item)}
                            accent="text-orange-700"
                          />
                        </div>

                        {item.approvalStatus === EPlanCouponApprovalStatus.Rejected && item.rejectionReason && (
                          <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                            {item.rejectionReason}
                          </div>
                        )}

                        {isPending && (
                          <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
                            <button
                              type="button"
                              className="inline-flex min-h-[34px] items-center justify-center gap-1 rounded-lg border border-emerald-500 bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-emerald-700"
                              onClick={(event) => {
                                event.stopPropagation()
                                handleApproveCoupon(item.id, refresh)
                              }}
                              data-interaction-description-key="interactionDescriptions:pages.adminDashboard.resellerCouponsPopup.action.approve"
                            >
                              <FontAwesomeIcon icon={faCheck} />
                              <span>{t("frontend:admin_dashboard.plans_and_coupons.coupon_approve_action")}</span>
                            </button>

                            <button
                              type="button"
                              className="inline-flex min-h-[34px] items-center justify-center gap-1 rounded-lg border border-red-400 bg-red-50 px-3 py-1.5 text-xs font-bold text-red-700 transition-colors hover:bg-red-100"
                              onClick={(event) => {
                                event.stopPropagation()

                                const reason = window.prompt(
                                  t("frontend:admin_dashboard.plans_and_coupons.coupon_reject_prompt"),
                                )

                                if (reason === null) return

                                handleRejectCoupon(item.id, reason, refresh)
                              }}
                              data-interaction-description-key="interactionDescriptions:pages.adminDashboard.resellerCouponsPopup.action.reject"
                            >
                              <FontAwesomeIcon icon={faXmark} />
                              <span>{t("frontend:admin_dashboard.plans_and_coupons.coupon_reject_action")}</span>
                            </button>
                          </div>
                        )}
                      </AdminListCard>
                    ),
                  }
                })}
              />
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
